/* OnSumn — WHAT FOLLOWS YOU (account-lite panel)
 *
 * The server-side twin of WHAT THIS BROWSER KNOWS. One footer door opens
 * this; nothing else, anywhere, ever opens it. Design: Zora's
 * 2026-08-26 lite-spec (§1 placement, §2 states, §3 chrome, §5 edge set,
 * §6 Iris's a11y gate). Copy: Rhea's FINAL strings, byte-verbatim — every
 * user-facing string on this surface lives in AFY_COPY below and nowhere
 * else, so a Rhea diff is one block.
 *
 * WHAT THIS IS NOT, BY CONSTRUCTION (Off-Limits §9 + G4):
 *   · not a modal — plain in-flow <section>, no role="dialog", no
 *     aria-modal, no scrim, no portal, no position:fixed, no focus trap.
 *   · not self-opening — mounts ONLY on a door activation. No deep link,
 *     no scroll trigger, no timer, no first-visit anything.
 *   · not an "account" — the word renders nowhere (Marco's ruling). The
 *     noun is "setup".
 *
 * WHERE ITS NUMBERS COME FROM (G6 reconciliation, §8.2): the rows read the
 * SAME LOCAL STORES the rest of the site reads — peekSavedTaste,
 * readInterested, readHomeBase — because lib/account-sync.js makes local a
 * write-through cache of the profile. This panel never reads profile taste;
 * nothing outside the sync layer does. That is why a hydrated browser
 * cannot mint a count this panel contradicts: there is only one store to
 * disagree with, and everybody reads it.
 *
 * DIGITS (G5 + 16 CFR 465, scoped): every STATIC string here is digit-free.
 * The only digits that can render are first-person record counts, and each
 * one is computed from the reader it mirrors — no audience number, no
 * membership number, no "join N" shape can reach this surface.
 */

/* Rhea FINAL, 2026-08-26. Do not paraphrase, improve, or pluralise by hand.
   The pinned honesty block is C3/C4 load-bearing: it kills the implied email
   promise AND names the granted capability (see, change, wipe) so the
   disclosed capability is a superset of the granted one. */
const AFY_COPY = {
  title: 'WHAT FOLLOWS YOU',
  doorLabel: 'WHAT FOLLOWS YOU',
  doorAttachedSuffix: '— setup attached',
  flowsDoor: 'CARRY IT ACROSS PHONES',

  stateA: 'Right now this lives in this browser only.',
  // Split only for the two real links; the concatenation is byte-identical
  // to Rhea's sentence and a test pins the joined textContent.
  stateB1: 'Nothing saved in this browser yet. Set a home station on ',
  stateBLinkFlows: 'Bay Flows',
  stateB2: ', or ',
  stateBLinkTaste: 'tune your wave',
  stateB3: ' — then it can follow you.',
  reattach: 'Set it up on another phone already? The same email brings it here.',
  attachLine: 'This browser carries the setup for',

  labelHome: 'HOME STATION',
  labelWave: 'YOUR WAVE',
  labelMarks: 'MARKS',
  emptyHome: 'No home station yet',
  emptyWave: 'No wave tuned yet',
  emptyMarks: 'No marks yet',

  doorHomeSet: 'CHANGE ON BAY FLOWS →',
  doorHomeEmpty: 'SET ON BAY FLOWS →',
  doorWaveSet: 'RETUNE →',
  doorWaveEmpty: 'TUNE →',
  remove: 'REMOVE',
  removeHomeLabel: 'Remove home station from your setup.',
  removeWaveLabel: 'Remove your wave from your setup.',
  removeMarksLabel: 'Remove your marks from your setup.',
  removedHome: 'Home station removed from your setup.',
  removedWave: 'Wave removed from your setup.',
  removedMarks: 'Marks removed from your setup.',

  fieldLabel: 'EMAIL',
  submit: 'CARRY IT',
  submitBusy: 'SAVING…',
  invalid: "That doesn't look like an email — check it and try again.",
  error: "Couldn't save it just now. Try again in a minute.",
  claimed: 'This browser now carries your setup.',

  honest: "Nothing gets emailed — the address is just the key. There's no password on it, so anyone who types this address can see this setup, change it, or wipe it; real sign-in comes later.",

  exportBtn: 'DOWNLOAD A COPY',
  signOut: 'SIGN OUT',
  deleteBtn: 'DELETE MY SETUP',
  signedOut: "This browser is off your setup. What's saved here still lives here.",

  confirmSentence: "This deletes your setup from our records — home station, wave, and marks. What's saved in this browser stays.",
  destroy: 'DELETE IT',
  keep: 'KEEP IT',
  deleted: 'Your setup is deleted from our records.',

  close: 'CLOSE',
  closeLabel: 'Close',

  // Scope sub-lines — same string on both surfaces, rendered by FlowsApp
  // and TasteProfileOverlay while a setup is attached (G6: the user must be
  // able to predict a destructive action's reach).
  scopeLine: 'Clears it here and from your setup.',
};

/* Station id → display name. Reuses BrowserKnows' presenter so the twin
   surfaces spell one station one way; guarded, because not every page ships
   BrowserKnows and a panel that invents a name is worse than one that shows
   the raw id (Atlas hole-1 discipline). */
function afyStationName(id) {
  if (typeof id !== 'string' || !id) return null;
  if (typeof window !== 'undefined' && typeof window.bkStationName === 'function') {
    return window.bkStationName(id);
  }
  return id;
}

/* First-person counts, singular/plural pinned (Rhea). Never a total, never
   an audience — the subject is always this browser's own record. */
function afyWaveValue(taste) {
  if (!taste || taste.skipped === true) return '';
  const vibes = Array.isArray(taste.vibes) ? taste.vibes.length : 0;
  const sounds = Array.isArray(taste.sounds) ? taste.sounds.length : 0;
  const parts = [];
  if (vibes > 0) parts.push(`${vibes} ${vibes === 1 ? 'vibe' : 'vibes'}`);
  if (sounds > 0) parts.push(`${sounds} ${sounds === 1 ? 'sound' : 'sounds'}`);
  return parts.join(', ');
}

function afyMarksValue(n, attached) {
  if (!n) return '';
  if (!attached) return `${n} ${n === 1 ? 'show' : 'shows'}`;
  return n === 1 ? '1 show follows you' : `${n} shows follow you`;
}

/* The three rows, read from the local stores this panel mirrors.
 *
 * CROSS-PAGE TRUTH (BrowserKnows' Atlas hole-1 rule, applied to a different
 * contract): not every page ships every reader — dist/TasteProfile.js is
 * homepage-only by Marco's placement ruling, so /venue and /flows cannot
 * read the wave. A row whose reader is ABSENT renders NOTHING rather than
 * "No wave tuned yet", because a panel that reports a false absence is worse
 * than a panel that stays quiet about what it cannot see. The row is fully
 * present on the homepage, which is ≤2 taps from anywhere via the same
 * footer door.
 *
 * The clean fix is `lib/taste-read.js` — the exact extraction
 * lib/interest-read.js already got, for exactly this reason. Flagged as
 * follow-up rather than done here: the taste parser is coupled to the
 * picker's vocabulary constants, so pulling it out is its own change with
 * its own gates, not a ride-along on this one. */
function afyRows(attached) {
  const canHome = typeof window !== 'undefined' && typeof window.readHomeBase === 'function';
  const canTaste = typeof window !== 'undefined' && typeof window.peekSavedTaste === 'function';
  const canMarks = typeof window !== 'undefined' && typeof window.readInterested === 'function';
  const home = canHome ? window.readHomeBase() : null;
  const taste = canTaste ? window.peekSavedTaste() : null;
  const marks = canMarks ? window.readInterested() : [];

  return [
    {
      field: 'homeStation',
      readable: canHome,
      label: AFY_COPY.labelHome,
      value: (home && home.stationId) ? afyStationName(home.stationId) : '',
      empty: AFY_COPY.emptyHome,
      doorSet: AFY_COPY.doorHomeSet,
      doorEmpty: AFY_COPY.doorHomeEmpty,
      href: '/flows',
      removeLabel: AFY_COPY.removeHomeLabel,
      removed: AFY_COPY.removedHome,
    },
    {
      field: 'taste',
      readable: canTaste,
      label: AFY_COPY.labelWave,
      value: afyWaveValue(taste),
      empty: AFY_COPY.emptyWave,
      doorSet: AFY_COPY.doorWaveSet,
      doorEmpty: AFY_COPY.doorWaveEmpty,
      href: '/',
      opensTasteEditor: true,
      removeLabel: AFY_COPY.removeWaveLabel,
      removed: AFY_COPY.removedWave,
    },
    {
      field: 'marks',
      readable: canMarks,
      label: AFY_COPY.labelMarks,
      value: afyMarksValue(Array.isArray(marks) ? marks.length : 0, attached),
      empty: AFY_COPY.emptyMarks,
      // Marks are made on cards. A door here would be a second editor for
      // one datum — the UI twin of the one-parser-one-file doctrine.
      doorSet: null,
      doorEmpty: null,
      removeLabel: AFY_COPY.removeMarksLabel,
      removed: AFY_COPY.removedMarks,
    },
  ];
}

function AccountLitePanel({ darkMode = true, radius = 14, onClose, scrollOnMount = false }) {
  const acct = (typeof window !== 'undefined' && window.OnSumnAccount) ? window.OnSumnAccount : null;

  const [tick, setTick] = React.useState(0);
  const [phase, setPhase] = React.useState('idle'); // idle|sending|invalid|error
  const [email, setEmail] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [attached, setAttached] = React.useState(() => !!(acct && acct.isAttached()));
  const [claimedEmail, setClaimedEmail] = React.useState(
    () => (acct && acct.readAccount() ? acct.readAccount().email : '')
  );

  const rootRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  const aliveRef = React.useRef(true);

  const reduce = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const rows = React.useMemo(() => afyRows(attached), [tick, attached]); // eslint-disable-line
  const hasLocal = rows.some((r) => r.readable && !!r.value);

  /* Mount: focus the title. No entrance animation — brutalist objects
     arrive, they do not perform. The contextual door on /flows asks for the
     scroll; reduced motion drops it to an instant jump. */
  React.useEffect(() => {
    aliveRef.current = true;
    const node = rootRef.current;
    if (scrollOnMount && node && typeof node.scrollIntoView === 'function') {
      try { node.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); } catch (e) { node.scrollIntoView(); }
    }
    if (titleRef.current) {
      try { titleRef.current.focus({ preventScroll: !!scrollOnMount }); } catch (e) { titleRef.current.focus(); }
    }
    return () => { aliveRef.current = false; };
  }, []); // eslint-disable-line

  /* Attached: reconcile with the server once. Silent either way — an
     unreachable profile is not an error surface (R5/E7), it is simply the
     current shipped site. */
  React.useEffect(() => {
    if (!acct || !acct.isAttached()) return;
    let cancelled = false;
    acct.ensureHydrated().then((profile) => {
      if (cancelled || !aliveRef.current) return;
      const rec = acct.readAccount();
      setAttached(!!rec);
      setClaimedEmail(rec ? ((profile && profile.email) || rec.email) : '');
      setTick((t) => t + 1);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  /* Esc closes and hands focus back to the door. No trap — the panel is
     in-flow; Tab simply walks on into the footer. */
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (!rootRef.current || !rootRef.current.contains(document.activeElement)) return;
      e.stopPropagation();
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  /* Deliberate focus after every unmounting activation (Iris). A vanished
     target falls back to the panel title, which always exists. */
  const pendingFocus = React.useRef(null);
  React.useEffect(() => {
    const sel = pendingFocus.current;
    if (!sel || !rootRef.current) return;
    pendingFocus.current = null;
    const el = rootRef.current.querySelector(sel);
    if (el) el.focus();
    else if (titleRef.current) titleRef.current.focus();
  });

  /* ── Palette (Zora §2 / Iris §6; every pair COMPUTED, both themes, by
     tests/account-lite-panel.test.js via lib/glass-contrast.js — never a
     substring check, because a substring check once shipped a 2.14:1
     border). Flat paper, no glass: the transparency family reads as paper.
       dark  ink #FFFFFF on #0A0A0A = 19.80 · quiet .6 = 7.30 · faint .48 = 4.99
       light ink #0A0A0A on #FAF7F1 = 18.52 · quiet .55 = 4.68 · faint = 4.68 */
  const paper = darkMode ? '#0A0A0A' : '#FAF7F1';
  const ink = darkMode ? '#FFFFFF' : '#0A0A0A';
  const quiet = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
  const faint = darkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.55)';
  const hairline = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const fieldBg = darkMode ? 'rgba(255,255,255,0.06)' : '#FFFFFF';
  const fieldBorder = darkMode ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.45)';
  const btnBg = darkMode ? '#FFFFFF' : '#0A0A0A';
  const btnInk = darkMode ? '#0A0A0A' : '#FFFFFF';
  const pillBorder = darkMode ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(0,0,0,0.25)';
  const pillInk = darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)';
  const colorMotion = reduce ? 'none' : 'background 120ms ease, color 120ms ease';

  const busy = phase === 'sending';

  const labelType = {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase',
  };
  const capsPill = {
    minHeight: 48, padding: '0 18px', borderRadius: 100,
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    background: 'transparent', border: pillBorder, color: pillInk,
    cursor: 'pointer', transition: colorMotion,
  };

  function announce(message) { setStatus(message); }

  function refresh() { setTick((t) => t + 1); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy || !acct) return;
    if (!acct.validEmail(email)) { setPhase('invalid'); announce(AFY_COPY.invalid); return; }
    setPhase('sending');
    announce('');
    let res = null;
    try { res = await acct.claim(email); } catch (err) { res = { ok: false, reason: 'error' }; }
    if (!aliveRef.current) return;
    if (res && res.ok) {
      setPhase('idle');
      setEmail('');
      setAttached(true);
      setClaimedEmail((res.profile && res.profile.email) || acct.normalizeEmail(email));
      // T5: the merge is silent. One line, no count, no "restored", no
      // "welcome back", no "synced" — the rows above simply populate.
      announce(AFY_COPY.claimed);
      refresh();
      return;
    }
    setPhase(res && res.reason === 'invalid' ? 'invalid' : 'error');
    announce(res && res.reason === 'invalid' ? AFY_COPY.invalid : AFY_COPY.error);
  }

  async function handleRemoveField(row) {
    if (!acct) return;
    // Server first, then the local mirror — the panel is the ground truth
    // for both halves, and a removal that only cleared one would resurrect.
    try { await acct.removeField(row.field); } catch (e) {}
    try {
      if (row.field === 'homeStation' && typeof window.forgetHomeBase === 'function') window.forgetHomeBase();
      else if (row.field === 'taste') localStorage.removeItem('onsumn-taste');
      else if (row.field === 'marks') localStorage.removeItem('onsumn-interests');
    } catch (e) {}
    if (!aliveRef.current) return;
    pendingFocus.current = '[data-afy-export]';
    announce(row.removed);
    refresh();
  }

  function handleSignOut() {
    if (!acct) return;
    acct.signOut();
    setAttached(false);
    setClaimedEmail('');
    setConfirming(false);
    // C5: one tap, no confirm, no side effect beyond the token. What is
    // saved in this browser is the browser's own and stays put.
    announce(AFY_COPY.signedOut);
    refresh();
  }

  function handleConfirmOpen() {
    pendingFocus.current = '[data-afy-confirm-sentence]';
    setConfirming(true);
  }

  function handleKeep() {
    pendingFocus.current = '[data-afy-delete]';
    setConfirming(false);
  }

  async function handleDestroy() {
    if (!acct) return;
    try { await acct.deleteSetup(); } catch (e) {}
    if (!aliveRef.current) return;
    setAttached(false);
    setClaimedEmail('');
    setConfirming(false);
    pendingFocus.current = '#afy-title';
    announce(AFY_COPY.deleted);
    refresh();
  }

  function handleExport() {
    // No argument on purpose: the sync layer holds the profile and writes the
    // file. This component never takes a profile object into its hands, which
    // is what keeps the single-reader fence a one-file claim rather than a
    // two-file one.
    if (acct) acct.downloadExport();
  }

  function openTasteEditor(e) {
    // One editor per datum: the panel never duplicates the picker. On the
    // homepage it opens the shipped TasteProfileOverlay; anywhere else the
    // anchor simply goes home, where that editor lives.
    if (typeof window === 'undefined' || typeof window.TasteProfileOverlay !== 'function') return;
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('onsumn:open-taste'));
    closeRef.current();
  }

  const linkStyle = {
    color: 'inherit', textDecoration: 'underline dotted', textUnderlineOffset: 3,
    transition: colorMotion,
  };
  const linkHandlers = {
    onMouseEnter: (e) => { e.currentTarget.style.color = 'var(--amber-gold-ink)'; },
    onMouseLeave: (e) => { e.currentTarget.style.color = 'inherit'; },
    onFocus: (e) => { e.currentTarget.style.color = 'var(--amber-gold-ink)'; },
    onBlur: (e) => { e.currentTarget.style.color = 'inherit'; },
  };

  /* Signed out: only populated rows (no false absences). Attached: all three
     honest empties too, each with its door — except a row this page cannot
     read at all, which stays silent either way. */
  const visibleRows = rows.filter((r) => r.readable && (attached || !!r.value));

  return (
    <section
      id="what-follows-you"
      ref={rootRef}
      data-afy-section
      aria-labelledby="afy-title"
      style={{ padding: '56px 32px', scrollMarginTop: 88 }}
    >
      <div data-afy-panel style={{
        maxWidth: 640, margin: '0 auto',
        background: paper, border: hairline, borderRadius: radius,
        padding: '32px 28px',
      }}>
        <h2 id="afy-title" ref={titleRef} tabIndex={-1} style={{
          fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 900,
          color: ink, textTransform: 'uppercase', letterSpacing: '0.02em',
          margin: '0 0 10px', outline: 'none',
        }}>{AFY_COPY.title}</h2>

        {/* One role=status region, present from FIRST render with a reserved
            line (Subscribe precedent): a screen reader announces a CHANGE,
            never a region appearing. It sits directly under the title —
            which is also where focus lands after a completed delete — and
            deliberately NOT between the claim form and the honesty block
            (§8.1 C1: nothing may come between those two). */}
        <p role="status" aria-live="polite" data-afy-status id="afy-status" style={{
          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
          color: quiet, minHeight: 20, margin: '0 0 8px',
        }}>{status}</p>

        {attached ? (
          <React.Fragment>
            <p data-afy-lead style={{
              fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
              color: quiet, margin: '0 0 2px',
            }}>{AFY_COPY.attachLine}</p>
            {/* The claimed address renders HERE and nowhere else on the
                site — never in chrome, never in a badge. */}
            <p data-afy-email style={{
              fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: '21px',
              color: ink, margin: '0 0 18px', wordBreak: 'break-all',
            }}>{claimedEmail}</p>
          </React.Fragment>
        ) : hasLocal ? (
          <p data-afy-lead style={{
            fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
            color: quiet, margin: '0 0 18px',
          }}>{AFY_COPY.stateA}</p>
        ) : (
          <p data-afy-lead style={{
            fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
            color: quiet, margin: '0 0 18px',
          }}>
            {AFY_COPY.stateB1}
            <a href="/flows" style={linkStyle} {...linkHandlers}>{AFY_COPY.stateBLinkFlows}</a>
            {AFY_COPY.stateB2}
            <a href="/" onClick={openTasteEditor} style={linkStyle} {...linkHandlers}>{AFY_COPY.stateBLinkTaste}</a>
            {AFY_COPY.stateB3}
          </p>
        )}

        {/* Record rows. Signed out, an empty row renders NOTHING — a false
            absence is worse than no row (BrowserKnows' Atlas hole-1 rule).
            Attached, all three render including their honest empties, each
            with the door to the one editor that owns that datum. */}
        {visibleRows.length > 0 && (
          <div data-afy-rows>
            {visibleRows.map((r) => {
              const populated = !!r.value;
              const door = populated ? r.doorSet : r.doorEmpty;
              return (
                <div key={r.field} data-afy-row={r.field} className="afy-row" style={{
                  padding: '12px 0', borderTop: hairline,
                }}>
                  <div style={{ ...labelType, color: faint, marginBottom: 5 }}>{r.label}</div>
                  <div className="afy-row-body" style={{
                    display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                  }}>
                    <div data-afy-value style={{
                      flex: '1 1 200px', minWidth: 0,
                      fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: '21px',
                      color: populated ? ink : quiet,
                    }}>{populated ? r.value : r.empty}</div>
                    {attached && door && (
                      <a
                        className="focus-double-ring"
                        data-afy-door={r.field}
                        href={r.href}
                        onClick={r.opensTasteEditor ? openTasteEditor : undefined}
                        style={{
                          ...labelType, fontSize: 11, color: quiet,
                          display: 'inline-flex', alignItems: 'center', minHeight: 44,
                          padding: '0 2px', textDecoration: 'underline dotted',
                          textUnderlineOffset: 3, transition: colorMotion,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = ink; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = quiet; }}
                        onFocus={(e) => { e.currentTarget.style.color = ink; }}
                        onBlur={(e) => { e.currentTarget.style.color = quiet; }}
                      >{door}</a>
                    )}
                    {attached && populated && (
                      /* G6: every populated datum is individually removable,
                         server-side, from the surface that discloses it. */
                      <button
                        type="button"
                        className="focus-double-ring"
                        data-afy-remove={r.field}
                        aria-label={r.removeLabel}
                        onClick={() => handleRemoveField(r)}
                        style={{
                          ...labelType, fontSize: 11, color: faint,
                          minHeight: 44, padding: '0 8px',
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          transition: colorMotion,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = ink; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = faint; }}
                        onFocus={(e) => { e.currentTarget.style.color = ink; }}
                        onBlur={(e) => { e.currentTarget.style.color = faint; }}
                      >{AFY_COPY.remove}</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {attached ? (
          !confirming ? (
            <div data-afy-actions style={{
              display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
              margin: '18px 0 0', paddingTop: 14, borderTop: hairline,
            }}>
              <button type="button" className="focus-double-ring" data-afy-export
                onClick={handleExport} style={capsPill}>{AFY_COPY.exportBtn}</button>
              <button type="button" className="focus-double-ring" data-afy-signout
                onClick={handleSignOut} style={capsPill}>{AFY_COPY.signOut}</button>
              <button type="button" className="focus-double-ring" data-afy-delete
                onClick={handleConfirmOpen} style={capsPill}>{AFY_COPY.deleteBtn}</button>
            </div>
          ) : (
            /* One confirm. No second dialog, no retention offer, no undo, no
               interrogative (lexicon-banned), and the delete path is never
               instrumented. Focus lands on the SENTENCE — never on the
               destructive button, where muscle memory double-activates. */
            <div data-afy-confirm style={{ margin: '18px 0 0', paddingTop: 14, borderTop: hairline }}>
              <p data-afy-confirm-sentence tabIndex={-1} style={{
                fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: '21px',
                color: ink, margin: '0 0 12px', outline: 'none',
              }}>{AFY_COPY.confirmSentence}</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="focus-double-ring"
                  data-afy-destroy
                  onClick={handleDestroy}
                  style={{
                    minHeight: 48, padding: '0 20px', borderRadius: 100,
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: 'var(--international-orange)', color: '#10110F',
                    border: 'none', cursor: 'pointer',
                  }}
                >{AFY_COPY.destroy}</button>
                {/* One-sided symmetry (amendment §3.3): the decline is never
                    LOUDER than the destructive option — same size, same
                    weight, no fill, no border, and a computed contrast at or
                    below the accept's in BOTH themes. */}
                <button
                  type="button"
                  className="focus-double-ring"
                  data-afy-keep
                  onClick={handleKeep}
                  style={{
                    minHeight: 48, padding: '0 14px', borderRadius: 100,
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: 'transparent', color: faint,
                    border: 'none', cursor: 'pointer',
                  }}
                >{AFY_COPY.keep}</button>
              </div>
            </div>
          )
        ) : (
          <form data-afy-form noValidate onSubmit={handleSubmit} style={{ margin: '18px 0 0' }}>
            {!hasLocal && (
              /* State B is also the RE-ATTACH surface — one form, both
                 directions; the server answers claim-or-create in one shape. */
              <p data-afy-reattach style={{
                fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
                color: quiet, margin: '0 0 12px',
              }}>{AFY_COPY.reattach}</p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 220px' }}>
                <label htmlFor="afy-email" style={{ ...labelType, display: 'block', color: quiet, marginBottom: 8 }}>
                  {AFY_COPY.fieldLabel}
                </label>
                <input
                  id="afy-email"
                  className="focus-double-ring"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (phase === 'invalid') { setPhase('idle'); setStatus(''); } }}
                  /* The honesty block DESCRIBES the field: consent to the
                     exposure is adjacent to the act of creating it. */
                  aria-describedby="afy-honest afy-status"
                  aria-invalid={phase === 'invalid' ? 'true' : undefined}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    fontSize: 16, minHeight: 44, // 16px: iOS zoom floor, not taste
                    fontFamily: 'var(--font-body)', color: ink,
                    background: fieldBg, border: fieldBorder, borderRadius: 10,
                    padding: '10px 14px',
                  }}
                />
              </div>
              <button
                type="submit"
                className="focus-double-ring"
                data-afy-submit
                /* aria-disabled, never native disabled: native drops focus to
                   <body> for the send window. The busy guard above already
                   stops the double submit. */
                aria-disabled={busy ? 'true' : undefined}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: btnInk, background: btnBg,
                  border: 'none', borderRadius: 10,
                  minHeight: 44, padding: '10px 22px',
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
              >{busy ? AFY_COPY.submitBusy : AFY_COPY.submit}</button>
            </div>
          </form>
        )}

        {/* THE PINNED HONESTY BLOCK — renders in full in every state, A, B
            and C alike: the fact does not stop being true after you claim.
            Nothing renders between it and the claim form (§8.1 C1). */}
        <p data-afy-honest id="afy-honest" style={{
          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
          color: quiet, margin: '16px 0 0', paddingTop: 14, borderTop: hairline,
        }}>{AFY_COPY.honest}</p>

        <div style={{ margin: '14px 0 0' }}>
          <button
            type="button"
            className="focus-double-ring"
            data-afy-close
            aria-label={AFY_COPY.closeLabel}
            onClick={() => closeRef.current()}
            style={{
              ...labelType, fontSize: 11, color: faint,
              minHeight: 44, padding: '0 2px',
              background: 'none', border: 'none', cursor: 'pointer',
              textDecoration: 'underline dotted', textUnderlineOffset: 3,
              transition: colorMotion,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = faint; }}
            onFocus={(e) => { e.currentTarget.style.color = ink; }}
            onBlur={(e) => { e.currentTarget.style.color = faint; }}
          >{AFY_COPY.close}</button>
        </div>
      </div>
    </section>
  );
}

AccountLitePanel.propTypes = {
  darkMode: PropTypes.bool,
  radius: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  scrollOnMount: PropTypes.bool,
};

window.AFY_COPY = AFY_COPY;
window.afyWaveValue = afyWaveValue;
window.afyMarksValue = afyMarksValue;
window.AccountLitePanel = AccountLitePanel;
